import { Vec2, type Constraint, type Entity, type Layer } from '@lakaran/geometry';
import type { CadDocument } from './document';

const VEC_TAG = '__vec';

/** Native project file extension. */
export const PROJECT_EXTENSION = '.lakar';
export const PROJECT_FORMAT = 'lakar';
const LEGACY_FORMAT = 'lakaran-lk';

export interface DocumentFile {
  format: typeof PROJECT_FORMAT;
  version: 1;
  /** `procedural` when the project carries a node graph. */
  mode: 'flat' | 'procedural';
  currentLayerId: string;
  layers: Layer[];
  entities: Entity[];
  /** Modelling constraints; optional so older files still load. */
  constraints?: Constraint[];
  /**
   * Authoring payloads that do not belong to the geometry kernel (today: the
   * procedural node graph). Serialised verbatim so the engine stays UI-agnostic.
   */
  extra?: Record<string, unknown>;
}

function replacer(_key: string, value: unknown): unknown {
  if (value instanceof Vec2) return { [VEC_TAG]: [value.x, value.y] };
  return value;
}

function revive(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(revive);
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const tagged = record[VEC_TAG];
    if (Array.isArray(tagged) && tagged.length === 2) {
      return Vec2.of(Number(tagged[0]), Number(tagged[1]));
    }
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(record)) out[key] = revive(record[key]);
    return out;
  }
  return value;
}

/**
 * Serialises a project: the drawing (flat modelling) plus whatever authoring payloads
 * the editor hands over — `extra.graph` holds the procedural node graph.
 */
export function serializeDocument(
  doc: CadDocument,
  extra?: Record<string, unknown>,
): string {
  const data: DocumentFile = {
    format: PROJECT_FORMAT,
    version: 1,
    mode: extra && Object.keys(extra).length > 0 ? 'procedural' : 'flat',
    currentLayerId: doc.currentLayerId,
    layers: [...doc.layers.values()],
    entities: doc.all(),
    constraints: [...doc.constraints.values()],
    ...(extra ? { extra } : {}),
  };
  return JSON.stringify(data, replacer, 2);
}

export function parseDocumentFile(text: string): DocumentFile {
  const raw: unknown = JSON.parse(text);
  const data = revive(raw) as DocumentFile;
  const format = (data as { format?: string }).format;
  if (!data || (format !== PROJECT_FORMAT && format !== LEGACY_FORMAT) || !Array.isArray(data.entities)) {
    throw new Error('Not a Lakaran project file.');
  }
  return data;
}

/** Replaces the document content with an already parsed project file. */
export function applyDocumentFile(doc: CadDocument, data: DocumentFile): number {
  doc.clear();
  doc.layers.clear();
  for (const layer of data.layers ?? []) doc.layers.set(layer.id, layer);
  doc.addMany(data.entities);
  for (const constraint of data.constraints ?? []) doc.constraints.set(constraint.id, constraint);
  doc.setCurrentLayer(data.currentLayerId ?? '0');
  doc.currentLayerId = data.currentLayerId ?? '0';
  doc.reindex();
  return data.entities.length;
}

/** Replaces the document content with a loaded `.lakar` file. */
export function loadDocument(doc: CadDocument, text: string): number {
  return applyDocumentFile(doc, parseDocumentFile(text));
}

/** Revives an entity array that was serialised with `Vec2` tagging (worker → main hand-off). */
export function parseEntities(text: string): Entity[] {
  const raw: unknown = JSON.parse(text);
  const value = revive(raw);
  return Array.isArray(value) ? (value as Entity[]) : [];
}

/** Serialises any value with the same `Vec2` tagging used by project files. */
export function stringifyTagged(value: unknown): string {
  return JSON.stringify(value, replacer);
}

/** Parses a value written by `stringifyTagged`. */
export function parseTagged<T>(text: string): T {
  return revive(JSON.parse(text)) as T;
}

/** Serialises just the given entities (used for partial worker hand-offs). */
export function serializeEntities(entities: readonly Entity[]): string {
  return stringifyTagged(entities);
}
